import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Lock, CheckCircle2, ArrowLeft, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/Components/ui/card';
import { Input } from '@/Components/ui/input';
import { Button } from '@/Components/ui/button';
import { Text, Heading } from '@/Components/ui/typography';
import { Label } from '@/Components/ui/label';
import { getApiUrl } from '../../../config/api';
import { getColor } from '@/config/theme';
import { cn } from '@/lib/utils';

function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState(true);
  const [token, setToken] = useState('');
  
  const { token: urlToken } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check for token in multiple places:
    // 1. URL params (when accessed directly from frontend)
    // 2. Query string (when redirected from backend)
    // 3. Hash fragment (alternative method)
    
    let resetToken = urlToken;
    
    if (!resetToken) {
      // Check query string
      const urlParams = new URLSearchParams(location.search);
      resetToken = urlParams.get('token');
    }
    
    if (!resetToken) {
      // Check hash fragment
      const hash = location.hash;
      if (hash && hash.includes('token=')) {
        resetToken = hash.split('token=')[1].split('&')[0];
      }
    }

    if (!resetToken || resetToken.length < 10) {
      setIsValidToken(false);
      setMessage('Invalid reset link. Please request a new password reset.');
      return;
    }

    setToken(resetToken);
    setIsValidToken(true);
  }, [urlToken, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setMessage('Passwords do not match. Please try again.');
      return;
    }

    if (password.length < 6) {
      setMessage('Password must be at least 6 characters long.');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch(getApiUrl(`/api/reset-password-employer/${token}`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password }),
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        setIsSuccess(true);
        setMessage('Password has been reset successfully! You can now login with your new password.');
      } else {
        setIsSuccess(false);
        setMessage(data.message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      setIsSuccess(false);
      setMessage('An error occurred. Please check your internet connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isValidToken) {
    return (
      <div className="min-h-screen w-full bg-background text-foreground flex flex-col justify-center items-center relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>

        {/* Logo Section - Top Left */}
        <div className="absolute top-6 left-6 md:top-8 md:left-8 z-10">
          <Link 
            to="/hirewithus"
            className="transition-opacity hover:opacity-80"
          >
            <img
              src="/Newzenalphablue.png"
              alt="SearchJob.ai Logo"
              className="h-8 md:h-10 w-auto object-contain drop-shadow-sm"
            />
          </Link>
        </div>
        
        <div className="flex justify-center items-center gap-12 max-w-7xl w-full flex-wrap px-6 py-12 md:flex-nowrap relative z-10">
          <Card className={cn(
            "flex flex-col w-full max-w-[480px] p-8 md:p-10",
            "bg-card border border-border/50",
            "shadow-xl shadow-primary/5",
            "transition-all duration-300",
            "hover:shadow-2xl hover:shadow-primary/10"
          )}>
            <CardContent className="w-full p-0 space-y-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-destructive/10 p-3">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                </div>
              </div>
              <Heading level="h2" className="text-2xl font-semibold mb-2">
                Invalid Reset Link
              </Heading>
              <Text size="base" variant="muted" className="mb-6">
                {message}
              </Text>
              <Button 
                onClick={() => navigate('/hirewithus/forgot-password')}
                variant="default"
                className="w-full"
                style={{
                  backgroundColor: getColor('primary'),
                  color: getColor('primaryForeground'),
                  borderColor: getColor('primary')
                }}
              >
                Request New Reset Link
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-background text-foreground flex flex-col justify-center items-center relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Logo Section - Top Left */}
      <div className="absolute top-6 left-6 md:top-8 md:left-8 z-10">
        <Link 
          to="/hirewithus"
          className="transition-opacity hover:opacity-80"
        >
          <img
            src="/Newzenalphablue.png"
            alt="SearchJob.ai Logo"
            className="h-8 md:h-10 w-auto object-contain drop-shadow-sm"
          />
        </Link>
      </div>
      
      <div className="flex justify-center items-center gap-12 max-w-7xl w-full flex-wrap px-6 py-12 md:flex-nowrap relative z-10">
        <Card className={cn(
          "flex flex-col w-full max-w-[480px] p-8 md:p-10",
          "bg-card border border-border/50",
          "shadow-xl shadow-primary/5",
          "transition-all duration-300",
          "hover:shadow-2xl hover:shadow-primary/10"
        )}>
          <CardContent className="w-full p-0 space-y-6">
            {!isSuccess ? (
              <>
                {/* Header */}
                <div className="space-y-3 mb-2">
                  <Heading level="h1" className="text-4xl font-bold text-foreground tracking-tight">
                    Reset Your Password
                  </Heading>
                  <Text size="base" variant="muted" className="text-muted-foreground leading-relaxed">
                    Enter your new password below.
                  </Text>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div className="space-y-4">
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="password" className="text-sm font-semibold">
                        New Password
                      </Label>
                      <div className="relative group/password">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10 transition-colors group-focus-within/password:text-primary" />
                        <Input
                          type="password"
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter new password"
                          required
                          className="pl-10 h-10 transition-all"
                          minLength={6}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <Label htmlFor="confirmPassword" className="text-sm font-semibold">
                        Confirm New Password
                      </Label>
                      <div className="relative group/confirm">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10 transition-colors group-focus-within/confirm:text-primary" />
                        <Input
                          type="password"
                          id="confirmPassword"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm new password"
                          required
                          className="pl-10 h-10 transition-all"
                          minLength={6}
                        />
                      </div>
                    </div>
                  </div>

                  {message && (
                    <div className={cn(
                      "rounded-md p-3 border",
                      isSuccess 
                        ? "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800"
                        : "bg-destructive/10 border-destructive/20"
                    )}>
                      <Text 
                        size="sm" 
                        variant={isSuccess ? "default" : "destructive"} 
                        className={isSuccess ? "text-green-800 dark:text-green-200" : "text-destructive"}
                      >
                        {message}
                      </Text>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    variant="default"
                    size="default"
                    className="w-full h-11 font-semibold shadow-sm hover:shadow-md transition-all duration-200"
                    style={{
                      backgroundColor: getColor('primary'),
                      color: getColor('primaryForeground'),
                      borderColor: getColor('primary')
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Resetting Password...' : 'Reset Password'}
                  </Button>

                  <div className="pt-2">
                    <Link 
                      to="/hirewithus/login" 
                      className={cn(
                        "flex items-center gap-2 text-sm text-muted-foreground hover:text-primary",
                        "transition-colors"
                      )}
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to Login
                    </Link>
                  </div>
                </form>
              </>
            ) : (
              <div className="text-center space-y-6">
                <div className="flex justify-center mb-4">
                  <div className="rounded-full bg-green-500/10 p-3">
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </div>
                </div>
                <Heading level="h2" className="text-2xl font-semibold mb-2">
                  Password Reset Successful!
                </Heading>
                <Text size="base" variant="muted" className="mb-6">
                  {message}
                </Text>
                <Button 
                  onClick={() => navigate('/hirewithus/login')}
                  variant="default"
                  className="w-full"
                  style={{
                    backgroundColor: getColor('primary'),
                    color: getColor('primaryForeground'),
                    borderColor: getColor('primary')
                  }}
                >
                  Go to Login
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ResetPassword;
